<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import CarUseModel from '@models/CarUse';
import CarModel from '@models/Car';
import CarTypeModel from '@models/CarType';
import CarOwnerModel from '@models/CarOwner';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import CarForm from '@views/cars/form';
import EntityMultiselect from '@components/EntityMultiselect';
import beforeLeaveAfterEdit from '@mixins/beforeLeaveAfterEditModal';
import FileWrapper from '@components/FileWrapper';
import FileDownloadLink from '@components/FileDownloadLink';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
    CarForm,
    EntityMultiselect,
    FileWrapper,
    FileDownloadLink,
  },
  mixins: [editFormChange, datePickerUtility, beforeLeaveAfterEdit],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
    },
  },
  data() {
    return {
      carModel: CarModel,
      showAddNewCarModal: false,
      selectedCar: false,
      carUse: {
        report_id: this.reportId,
        car_id: null,
        car: null,
        takeover_from: null,
        return_to: null,
        takeover_date: null,
        return_date: null,
        km_status_takeover: null,
        km_status_return: null,
        verified: 0,
        sva_print: 0,
      },
      carTypes: [],
      carOwnerModel: CarOwnerModel,
      defaultCarUse: {
        report_id: this.reportId,
        car_id: null,
        car: null,
        takeover_from: null,
        return_to: null,
        takeover_date: null,
        return_date: null,
        km_status_takeover: null,
        km_status_return: null,
        verified: 0,
        sva_print: 0,
      },
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfigTime,
    }),
  },
  mounted() {
    this.defaultCarUse = Object.assign({}, this.carUse);
    this.$wait.start('car types loading');
    CarTypeModel.select(['id', 'name'])
      .orderBy(`-name`)
      .page(1)
      .limit(100)
      .get()
      .then((response) => {
        this.carTypes = response.data;
        this.$wait.end('car types loading');
      })
      .catch(() => {
        this.$wait.end('car types loading');
      });
    if (this.loadedId) {
      this.$wait.start('car-use loading');
      new ReportModel({ id: this.reportId })
        .carUses()
        .find(this.loadedId)
        .then((response) => {
          this.carUse = Object.assign({}, this.carUse, response.car_use);
          this.$wait.end('car-use loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['carUse'], this.validate);
          });
        });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll('car-use-form').then((result) => {
        if (result) {

          if(!this.carUse.car.verified) {
            // TODO maybe use $ref and submit car form instead
            new CarModel(
              Object.assign({}, this.carUse.car, {
                zub: this.carUse.car.zub && Array.isArray(this.carUse.car.zub) ? this.carUse.car.zub[0] : this.carUse.car.zub,
              })
            )
              .save()
              .then((response) => {
                this.carUse.car = response.car;
                this.carUse.car_id = response.car.id;
                this.submit(callback);
              })
              .catch(() => this.$wait.end('car-use loading'));
          } else {
            this.submit(callback);
          }  
        }
      });
    },
    submit(callback) {
      const report = new ReportModel({ id: this.reportId });
      new CarUseModel(Object.assign({}, this.carUse))
        .for(report)
        .save()
        .then((response) => {
          this.carUse = Object.assign({}, this.defaultCarUse);
          this.$validator.reset();
          this.$emit('submitted', response.car_use);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('car-use loading');
        })
        .catch(() => this.$wait.end('car-use loading'));
    },
    hide(bvEvt) {
      this.beforeModalClose(bvEvt, () => {
        this.setUnchanged();
        this.$refs.carFormModal.hide();
      });
    },
    carSelected(car) {
      this.carUse.car = car;
      this.showAddNewCarModal = false;
    },
    removeZUB() {
      this.carUse.car.zub = [];
    }        
  },
};
</script>
<template>
  <div>
    <BlockingContainer v-if="$wait.is('car-use loading') || $wait.is('car types loading')">&nbsp;</BlockingContainer>
    <b-form data-vv-scope="car-use-form" @submit.prevent="validate">
      <b-row>
        <b-col :lg="loadedId != null ? 12 : 10" sm="12">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.license_plate') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <EntityMultiselect
              v-model="carUse.car_id"
              v-validate="'required'"
              data-vv-name="car_id"
              :data-vv-as="$t('fields.license_plate')"
              :initial-value="carUse.car"
              :state="errors.has('car_id', 'car-use-form')"
              :placeholder="$t('placeholders.license_plate_from_db')"
              sort="-license_plate"
              where="license_plate"
              label="license_plate"
              :model="carModel"
              @change-val="carSelected"
            >
            </EntityMultiselect>

            <div v-show="errors.has('car_id', 'car-use-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('car_id', 'car-use-form') }}
            </div>
          </b-form-group>
        </b-col>
        <b-col v-if="loadedId == null" lg="2" sm="12">
          <b-form-group>
            <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0">&nbsp;</legend>
            <b-btn variant="secondary" class="w-100 text-white" @click="showAddNewCarModal = true">
              {{ $t('titles.add_new_car') }}
            </b-btn>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-row v-if="carUse.car != null && !carUse.car.verified">
        <b-col sm=12>
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.zub') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="zubPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="zubPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.zub') }}
                </b-popover>
              </span>
            </template>
            <FileDownloadLink v-if="carUse.car.zub && !Array.isArray(carUse.car.zub)" :file="carUse.car.zub" :disabled="carUse.car.verified === 1" @remove="removeZUB" />
            <FileWrapper
              v-else
              v-model="carUse.car.zub"
              v-validate="{ image: true }"
              :disabled="carUse.car.verified === 1"
              :data-vv-as="$t('fields.zub')"
              :state="errors.has('zub', 'car-use-form') ? false : null"
              :placeholder="$t('placeholders.zub')"
              accept="image/*"
              aria-describedby="input-feedback-zub"
              name="zub"
            >
            </FileWrapper>

            <div v-show="errors.has('zub', 'car-use-form')" class="invalid-feedback d-block mt-1">
              {{ errors.first('zub', 'car-use-form') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.takeover_from') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="takeoverFromPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="takeoverFromPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.takeover_from') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="carUse.takeover_from"
              v-validate="'max:255'"
              data-vv-value-path="localValue"
              data-vv-name="takeover_from"
              :data-vv-as="$t('fields.takeover_from')"
              type="text"
              :state="errors.has('takeover_from', 'car-use-form') ? false : null"
              aria-describedby="input-feedback-takeover_from"
              :placeholder="$t('placeholders.takeover_from')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-takeover_from">
              <template v-show="errors.has('takeover_from', 'car-use-form')">{{ errors.first('takeover_from', 'car-use-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.return_to') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="returnToPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="returnToPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.return_to') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="carUse.return_to"
              v-validate="'max:255'"
              data-vv-value-path="localValue"
              data-vv-name="return_to"
              :data-vv-as="$t('fields.return_to')"
              type="text"
              :state="errors.has('return_to', 'car-use-form') ? false : null"
              aria-describedby="input-feedback-return_to"
              :placeholder="$t('placeholders.return_to')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-return_to">
              <template v-show="errors.has('return_to', 'car-use-form')">{{ errors.first('return_to', 'car-use-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.takeover_date') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="takeoverDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="takeoverDatePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.takeover_date') }}
                </b-popover>
              </span>
            </template>
            <div :class="{ 'is-invalid': errors.has('takeover_date', 'car-use-form') }">
              <flat-pickr
                v-model="carUse.takeover_date"
                v-validate="''"
                data-vv-name="takeover_date"
                :data-vv-as="$t('fields.takeover_date')"
                aria-describedby="input-feedback-takeover_date"
                :config="dateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('takeover_date', 'car-use-form') }"
                :placeholder="$t('placeholders.date')"
                @on-close="closeCalendar('carUse.takeover_date', 'dd.MM.yyyy HH:mm', 'yyyy-MM-dd HH:mm:ss', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-takeover_date">
                <template v-show="errors.has('takeover_date', 'car-use-form')">{{ errors.first('takeover_date', 'car-use-form') }} </template>
              </b-form-invalid-feedback>
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.return_date') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="returnDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="returnDatePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.return_date') }}
                </b-popover>
              </span>
            </template>
            <flat-pickr
              v-model="carUse.return_date"
              v-validate="''"
              data-vv-name="return_date"
              :data-vv-as="$t('fields.return_date')"
              aria-describedby="input-feedback-return_date"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('return_date', 'car-use-form') }"
              :placeholder="$t('placeholders.date')"
              @on-close="closeCalendar('carUse.return_date', 'dd.MM.yyyy HH:mm', 'yyyy-MM-dd HH:mm:ss', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-return_date">
              <template v-show="errors.has('return_date', 'car-use-form')">{{ errors.first('return_date', 'car-use-form') }} </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.km_status_takeover') }}:
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="kmTakeoverPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="kmTakeoverPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.km_status_takeover') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="carUse.km_status_takeover"
              v-validate="'integer'"
              data-vv-value-path="localValue"
              data-vv-name="km_status_takeover"
              :data-vv-as="$t('fields.km_status_takeover')"
              type="text"
              :state="errors.has('km_status_takeover', 'car-use-form') ? false : null"
              aria-describedby="input-feedback-km_status_takeover"
              :placeholder="$t('placeholders.km_status_takeover')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-km_status_takeover">
              <template v-show="errors.has('km_status_takeover', 'car-use-form')">{{ errors.first('km_status_takeover', 'car-use-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.km_status_return') }}:
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="kmReturnPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="kmReturnPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.km_status_return') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="carUse.km_status_return"
              v-validate="'integer'"
              data-vv-value-path="localValue"
              data-vv-name="km_status_return"
              :data-vv-as="$t('fields.km_status_return')"
              type="text"
              :state="errors.has('km_status_return', 'car-use-form') ? false : null"
              aria-describedby="input-feedback-km_status_return"
              :placeholder="$t('placeholders.km_status_return')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-km_status_return">
              <template v-show="errors.has('km_status_return', 'car-use-form')">{{ errors.first('km_status_return', 'car-use-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-button
        ref="carUseSubmitBtn"
        type="submit"
        :disabled="errors.any('car-use-form')"
        :variant="'outline-' + (!errors.any('car-use-form') ? 'dark' : 'danger')"
      >
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
    <b-modal
      ref="carFormModal"
      v-model="showAddNewCarModal"
      size="lg"
      hide-footer
      :title="$t('titles.add_new_car')"
      static
      @hide="hide" @hidden="$emit('hidden')">
      <CarForm @new-car="carSelected">&nbsp;</CarForm>
    </b-modal>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
